import { authService } from '@/services/auth'
import { TelegramAuthRequest, User } from '@/types'
import { authDebug } from '@/composables/useAuthDebug'
import { useWineStore } from '@/stores/wine'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { telegramUser } from '@/utils/constants'

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null)
    const isAuthenticated = ref(false)
    const isLoading = ref(false)

    const isAdmin = computed(() => user.value?.role === 'admin')
    const isModerator = computed(() => user.value?.role === 'moderator' || isAdmin.value)

    /**
     * Синхронизирует состояние store с authService
     * Вызывается после успешной реавторизации
     */
    const syncWithAuthService = () => {
        const currentUser = authService.getCurrentUser()
        const authenticated = authService.isAuthenticated()

        user.value = currentUser
        isAuthenticated.value = authenticated

        console.log('🔄 Store synced with authService:', {
            authenticated,
            userId: currentUser?.id,
        })

        authDebug.addEvent('sync', 'Store synced with authService', {
            authenticated,
            userId: currentUser?.id,
            telegramId: currentUser?.telegram_id,
        })
        authDebug.updateCurrentUser(currentUser)
        authDebug.updateTokens(authService.getToken(), authService.getRefreshToken())
    }

    /**
     * Пытается авторизоваться через Telegram
     * @returns true если авторизация успешна, false если нет
     */
    const attemptTelegramAutoLogin = async (): Promise<boolean> => {
        console.log('🤖 Attempting Telegram auto-login...')
        authDebug.addEvent('login', 'Store: Attempting Telegram auto-login')

        // Хардкодим авторизацию и юзера
        const loginResult = await authService.loginWithTelegramUser(telegramUser)

        if (loginResult.success) {
            syncWithAuthService()
            console.log('✅ Telegram auto-login successful')
            authDebug.addEvent('login', 'Store: Telegram auto-login successful')
            return true
        } else {
            console.log('❌ Telegram auto-login failed:', loginResult.error)
            authDebug.addError(`Store: Telegram auto-login failed: ${loginResult.error}`)
            user.value = null
            isAuthenticated.value = false
            return false
        }
    }

    const init = async () => {
        isLoading.value = true
        console.log('🔐 Initializing auth store...')
        authDebug.addEvent('init', 'Store: Initializing auth store')

        try {
            // Регистрируем callback для синхронизации после реавторизации
            authService.setOnReauthSuccess(() => {
                syncWithAuthService()
            })

            const currentUser = authService.getCurrentUser()
            const authenticated = authService.isAuthenticated()

            authDebug.updateCurrentUser(currentUser)
            authDebug.updateTokens(authService.getToken(), authService.getRefreshToken())
            authDebug.refreshAll()

            // Если есть токен, но нет данных пользователя - попробуем обновить токен
            if (authenticated && !currentUser) {
                console.log('🔄 Token exists but no user data, trying refresh...')
                authDebug.addEvent('init', 'Store: Token exists but no user data, trying refresh')
                const refreshed = await authService.refreshAuthToken()
                if (refreshed) {
                    syncWithAuthService()
                    console.log('✅ Token refreshed and user data restored')
                    authDebug.addEvent('init', 'Store: Token refreshed and user data restored')
                } else {
                    console.log('❌ Token refresh failed, clearing auth')
                    authDebug.addError('Store: Token refresh failed, clearing auth')
                    // После неудачного refresh пробуем авторизоваться через Telegram
                    await attemptTelegramAutoLogin()
                }
            } else if (authenticated && currentUser) {
                // Проверяем, соответствует ли сохраненный пользователь текущему пользователю Telegram
                // В Telegram Web App всегда должен быть пользователь Telegram
                authDebug.addEvent('init', 'Store: Checking Telegram user match', {
                    savedTelegramId: currentUser.telegram_id,
                })
                const userMatch = authService.checkTelegramUserMatch()
                
                // Если у сохраненного пользователя нет telegram_id - это старые данные, нужно перелогиниться
                // Если telegram_id есть, но не совпадает - тоже нужно перелогиниться
                if (!currentUser.telegram_id || !userMatch) {
                    console.log('⚠️ Telegram user mismatch detected', {
                        savedTelegramId: currentUser.telegram_id,
                        hasTelegramId: !!currentUser.telegram_id,
                        userMatch: userMatch,
                    })
                    authDebug.addEvent('init', 'Store: Telegram user mismatch detected', {
                        savedTelegramId: currentUser.telegram_id,
                        reason: !currentUser.telegram_id ? 'Saved user has no telegram_id' : 'telegram_id mismatch',
                        userMatch: userMatch,
                    })
                    // Очищаем данные старого пользователя
                    await authService.logout()
                    // Очищаем данные вина перед перелогином
                    const wineStore = useWineStore()
                    wineStore.clearData()
                    // Пробуем авторизоваться под новым пользователем
                    await attemptTelegramAutoLogin()
                } else {
                    // Пользователь совпадает - используем сохраненные данные
                    authDebug.addEvent('init', 'Store: User match OK, using saved data', {
                        match: userMatch,
                    })
                    syncWithAuthService()
                }
            } else if (!authenticated) {
                // Если пользователь не авторизован - пробуем автологин через Telegram
                authDebug.addEvent('init', 'Store: Not authenticated, attempting auto-login')
                await attemptTelegramAutoLogin()
            } else {
                // Пользователь авторизован и данные есть - используем сохраненные данные
                authDebug.addEvent('init', 'Store: Using saved auth data')
                syncWithAuthService()
            }

            console.log('✅ Auth initialized:', {
                authenticated: isAuthenticated.value,
                user: user.value,
                hasToken: !!authService.getToken(),
            })
            authDebug.addEvent('init', 'Store: Auth initialization completed', {
                authenticated: isAuthenticated.value,
                userId: user.value?.id,
                telegramId: user.value?.telegram_id,
            })
        } catch (error) {
            console.error('Auth init error:', error)
            authDebug.addError(`Store: Auth init error: ${(error as Error).message || 'Unknown error'}`)
        } finally {
            isLoading.value = false
        }
    }

    const login = async () => {
        isLoading.value = true
        try {
            const result = await authService.loginWithTelegram()
            if (result.success) {
                syncWithAuthService()
                return { success: true }
            } else {
                return { success: false, error: result.error }
            }
        } catch (error: unknown) {
            console.error('Login error:', error)
            return { success: false, error: (error as Error).message }
        } finally {
            isLoading.value = false
        }
    }

    const loginWithCredentials = async (username: string, password: string) => {
        isLoading.value = true
        try {
            const result = await authService.loginWithCredentials(username, password)
            if (result.success) {
                syncWithAuthService()
                return { success: true }
            } else {
                return { success: false, error: result.error }
            }
        } catch (error: unknown) {
            console.error('Login error:', error)
            return { success: false, error: (error as Error).message }
        } finally {
            isLoading.value = false
        }
    }

    const loginWithTelegramUser = async (telegramUser: TelegramAuthRequest) => {
        isLoading.value = true
        try {
            const result = await authService.loginWithTelegramUser(telegramUser)
            if (result.success) {
                syncWithAuthService()
                return { success: true }
            } else {
                return { success: false, error: result.error }
            }
        } catch (error: unknown) {
            console.error('Telegram login error:', error)
            return { success: false, error: (error as Error).message }
        } finally {
            isLoading.value = false
        }
    }

    const logout = async () => {
        isLoading.value = true
        try {
            await authService.logout()
            
            // Очищаем данные вина при выходе
            const wineStore = useWineStore()
            wineStore.clearData()
            
            user.value = null
            isAuthenticated.value = false
            return { success: true }
        } catch (error: unknown) {
            console.error('Logout error:', error)
            return { success: false, error: (error as Error).message }
        } finally {
            isLoading.value = false
        }
    }

    const updateUser = async (userData: Partial<User>) => {
        isLoading.value = true
        try {
            const result = await authService.updateUser(userData)
            if (result.success) {
                syncWithAuthService()
                return { success: true }
            } else {
                return { success: false, error: result.error }
            }
        } catch (error: unknown) {
            console.error('Update user error:', error)
            return { success: false, error: (error as Error).message }
        } finally {
            isLoading.value = false
        }
    }

    const getUserStats = async () => {
        try {
            return await authService.getUserStats()
        } catch (error: unknown) {
            console.error('Get user stats error:', error)
            return { success: false, error: (error as Error).message }
        }
    }

    return {
        user,
        isAuthenticated,
        isLoading,
        isAdmin,
        isModerator,
        init,
        login,
        loginWithCredentials,
        loginWithTelegramUser,
        logout,
        updateUser,
        getUserStats,
    }
})
